/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import RenderPaymentCode from "./RenderPaymentCode";
import { mapPaymentMethodDataStatus, mapPaymentMethodData } from "../../../assets/helper.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const paymentMethods = [
  {
    key: "paypal_manual",
    label: "PayPal",
  }
];

const defaultFormData = {
  paypalManualOrgName: '',
  paypalManualInstructions: '',
  paypalManualLink: '',
};

class RenderPayPalManual extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      formData: defaultFormData,
    };

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.mapFormDataFromProps = this.mapFormDataFromProps.bind(this);
    this.mapFieldText = this.mapFieldText.bind(this);
    this.renderErrorMsg = this.renderErrorMsg.bind(this);
  }

  componentDidMount() {
    const newFormData = this.mapFormDataFromProps();

    this.setState({
      formData: newFormData,
    });
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapFormDataFromProps();
      this.setState({
        formData: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
    });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleSave() {
    const { formData } = this.state;
    const { func } = this.props;

    func.handleUpdatePaymentData(paymentMethods[0].key, paymentMethods[0].label, formData);

    this.setState({ isEditMode: false });
  }

  mapFormDataFromProps() {
    const { state } = this.props;
    const { formData } = this.state;

    let newFormData = formData;

    const paymentCodes = state.organizationObj.paymentMethodCode;

    if (paymentCodes) {
      const getPaymentData = paymentCodes[paymentMethods[0].key] || null;

      if (getPaymentData) {
        // reset to latest data from props
        newFormData = {
          paypalManualOrgName: getPaymentData.paypalManualOrgName || '',
          paypalManualInstructions: getPaymentData.paypalManualInstructions || '',
          paypalManualLink: getPaymentData.paypalManualLink || '',
        };
      } else {
        // reset to default
        newFormData = defaultFormData;
      }
    }

    return newFormData;
  }

  mapFieldText(field)  {
    const { formData } = this.state;

    let text = '-';

    if (formData[field] !== '') {
      text = formData[field];
    }

    return text;
  }

  renderErrorMsg() {
    const { state } = this.props;
    const errMsg = state.errMsgInfo.paymentCode;

    if (errMsg.method === paymentMethods[0].key && !errMsg.isCode) {
      return renderErrorMsg(errMsg.msg);
    }

    return null;
  }

  render() {
    const { state, func } = this.props;
    const { isEditMode, formData } = this.state;

    const paypalManualStatus = mapPaymentMethodDataStatus(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    const paypalManualData = mapPaymentMethodData(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    const paypalManualIsConfigured = Boolean(paypalManualData.paypalManualOrgName || paypalManualData.paypalManualInstructions);

    if (state.loading) {
      return (
        <div className="RenderCash-loading">
          <div className="RenderCash-spinner"></div>
        </div>
      );
    }

    const currentPlan = state.organizationObj.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";
    const isPremiumPlan = currentPlan === "premium";

    if (!isPremiumPlan) {
      return (
        <div className="RenderCash-container">
          <UpgradeNoticeCard onUpgradeClick={func.redirectToOrgBilling} />
        </div>
      );
    }

    return (
      <div className="RenderCash-container">
        <div className="RenderCash-content">
          <div className="RenderCash-toggleRow">
            <span className="RenderCash-toggleLabel">Enable PayPal</span>
            <ToggleSwitch
              checked={paypalManualStatus.checked}
              onChange={(e) => func.handleUpdatePaymentStatus(e, paymentMethods[0].key)}
              disabled={!paypalManualIsConfigured}
              disabledMessage={!paypalManualIsConfigured ? "Please fill in the required fields first" : ""}
            />
          </div>

          <div className="RenderCash-formContainer">
            <div className="RenderCash-savedCredentials">

              <div className="RenderCash-group">
                <div className="RenderCash-subheading">PayPal Account / Organization Name</div>
                {isEditMode ? (
                  <div className="RenderCash-inputFieldContainer">
                    <input
                      type="text"
                      name="paypalManualOrgName"
                      className="RenderCash-inputField"
                      placeholder="PayPal Account / Organization Name"
                      value={formData.paypalManualOrgName}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderCash-textValue">
                    {this.mapFieldText('paypalManualOrgName')}
                  </div>
                )}
              </div>

              <div className="RenderCash-group">
                <div className="RenderCash-subheading">Instructions</div>
                {isEditMode ? (
                  <div className="RenderCash-textAreaFieldContainer">
                    <textarea
                      type="text"
                      name="paypalManualInstructions"
                      className="RenderCash-textAreaField"
                      placeholder="Instructions for donors (e.g. PayPal email address)"
                      value={formData.paypalManualInstructions}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderCash-textValue">
                    {this.mapFieldText('paypalManualInstructions')}
                  </div>
                )}
              </div>

              <div className="RenderCash-group">
                <div className="RenderCash-subheading">PayPal Link</div>
                {isEditMode ? (
                  <div className="RenderCash-inputFieldContainer">
                    <input
                      type="text"
                      name="paypalManualLink"
                      className="RenderCash-inputField"
                      placeholder="https://www.paypal.com/..."
                      value={formData.paypalManualLink}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderCash-textValue">
                    {this.mapFieldText('paypalManualLink')}
                  </div>
                )}
              </div>

              <div className="RenderCash-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div className="RenderCash-subheading" style={{  marginBottom: '20px' }}>PayPal QR Code (Optional)</div>
                <RenderPaymentCode
                  state={state}
                  func={func}
                  paymentMethod={paymentMethods[0]}
                />
              </div>

            </div>
          </div>

          {this.renderErrorMsg()}
        </div>
      </div>
    );
  }
}

RenderPayPalManual.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
};

export default RenderPayPalManual;
